import React, { useEffect, useState } from "react";
import { UserPlus, Trash2, Users, Copy, RefreshCw, Check, ShieldCheck, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { PageHeader, Button, Badge, SearchInput, EmptyState, Card, Avatar } from "@/components/ui-em";
import InviteModal from "@/components/InviteModal";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showInvite, setShowInvite] = useState(false);
  const [resendModal, setResendModal] = useState(null);
  const [copied, setCopied] = useState(false);
  const { user: me } = useAuth();

  const load = () => {
    setLoading(true);
    api.get("/users").then((r) => setUsers(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const [accessFilter, setAccessFilter] = useState("all");
  const filtered = users.filter((u) =>
    (!q ? true : u.name.toLowerCase().includes(q.toLowerCase()) || u.email.toLowerCase().includes(q.toLowerCase())) &&
    (accessFilter === "all" ? true : accessFilter === "blocked" ? (u.access === "required" || u.access === "expired") : u.access === accessFilter)
  );
  const blockedCount = users.filter((u) => u.access === "required" || u.access === "expired").length;

  const exempt = async (u) => {
    if (!window.confirm(`Give ${u.name} free access (no payment required)?`)) return;
    try {
      await api.post(`/admin/users/${u.id}/exempt-payment`);
      toast.success(`${u.name} now has free access`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const AccessBadge = ({ u, size }) => {
    if (u.role === "admin") return <Badge variant="gold" size={size} uppercase>Admin</Badge>;
    if (u.access === "exempt") return <Badge variant="success" size={size} uppercase>Free access</Badge>;
    if (u.access === "paid") return <Badge variant="outline" size={size} uppercase>Paid until {u.paid_until ? new Date(u.paid_until).toLocaleDateString() : "—"}</Badge>;
    if (u.access === "expired") return <Badge variant="warn" size={size} uppercase>Expired</Badge>;
    return <Badge variant="warn" size={size} uppercase><AlertTriangle className="w-3 h-3 inline mr-1 -mt-0.5" />Payment required</Badge>;
  };
  const canExempt = (u) => u.role !== "admin" && u.access !== "exempt";

  const remove = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const resend = async (id, name) => {
    try {
      const { data } = await api.post(`/users/${id}/resend-invitation`);
      setResendModal({ name, invitation_url: data.invitation_url });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Invitation link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy — select and copy manually");
    }
  };

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Admin · People"
        title="Users"
        description="Invite leaders. They set their own password via a private link."
        actions={<Button icon={UserPlus} onClick={() => setShowInvite(true)} size="lg" data-testid="admin-add-user">Invite user</Button>}
      />

      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1"><SearchInput value={q} onChange={setQ} placeholder="Search users..." /></div>
        <div className="flex items-center gap-1 flex-wrap" data-testid="access-filter">
          {[["all", "All"], ["blocked", `Payment required${blockedCount ? ` (${blockedCount})` : ""}`], ["exempt", "Free access"], ["paid", "Paid"]].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setAccessFilter(k)}
              data-testid={`access-filter-${k}`}
              className={`px-3 py-1.5 rounded-full text-xs border ${accessFilter === k ? "border-gold/50 bg-gold/10 text-gold" : "border-white/10 text-zinc-400 hover:text-white hover:border-white/20"}`}
              style={{ transitionProperty: "color, background-color, border-color", transitionDuration: "160ms" }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-8 text-sm text-zinc-500">Loading...</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No users yet"
            description="Invite your first leader — they'll get a private link to activate their account."
            actionLabel="Invite first user"
            onAction={() => setShowInvite(true)}
            className="m-4"
          />
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold text-left">
                    <th className="px-6 py-4">User</th>
                    <th className="px-6 py-4">Role</th>
                    <th className="px-6 py-4">Status</th>
                    <th className="px-6 py-4">Access</th>
                    <th className="px-6 py-4">Joined</th>
                    <th className="px-6 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((u) => (
                    <tr key={u.id} className="border-t border-white/5 hover:bg-white/[0.02]" style={{ transitionProperty: "background-color", transitionDuration: "140ms" }}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <Avatar name={u.name} size="sm" />
                          <div>
                            <div className="text-white">{u.name}</div>
                            <div className="text-xs text-zinc-500">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <Badge variant={u.role === "admin" ? "gold" : "outline"} uppercase>{u.role}</Badge>
                      </td>
                      <td className="px-6 py-4">
                        <Badge variant={u.status === "pending" ? "warn" : "success"} uppercase>
                          {u.status === "pending" ? "Pending" : "Active"}
                        </Badge>
                      </td>
                      <td className="px-6 py-4" data-testid={`access-${u.id}`}><AccessBadge u={u} /></td>
                      <td className="px-6 py-4 text-xs text-zinc-500">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {canExempt(u) && (
                            <button
                              onClick={() => exempt(u)}
                              data-testid={`exempt-${u.id}`}
                              className="p-2 rounded-md text-zinc-500 hover:text-emerald-300 hover:bg-emerald-500/10"
                              style={{ transitionProperty: "color, background-color", transitionDuration: "160ms" }}
                              title="Grant free access"
                            >
                              <ShieldCheck className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {u.status === "pending" && (
                            <button
                              onClick={() => resend(u.id, u.name)}
                              data-testid={`resend-${u.id}`}
                              className="p-2 rounded-md text-zinc-500 hover:text-gold hover:bg-gold/5"
                              style={{ transitionProperty: "color, background-color", transitionDuration: "160ms" }}
                              title="Resend invitation"
                            >
                              <RefreshCw className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {u.id !== me?.id && (
                            <button
                              onClick={() => remove(u.id)}
                              className="p-2 rounded-md text-zinc-500 hover:text-red-300 hover:bg-red-500/10"
                              style={{ transitionProperty: "color, background-color", transitionDuration: "160ms" }}
                              title="Delete user"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-white/5">
              {filtered.map((u) => (
                <div key={u.id} className="p-4 flex items-center gap-3">
                  <Avatar name={u.name} size="sm" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{u.name}</div>
                    <div className="text-xs text-zinc-500 truncate">{u.email}</div>
                    <div className="flex items-center gap-1.5 mt-2">
                      <Badge variant={u.role === "admin" ? "gold" : "outline"} size="xs" uppercase>{u.role}</Badge>
                      <Badge variant={u.status === "pending" ? "warn" : "success"} size="xs" uppercase>{u.status === "pending" ? "Pending" : "Active"}</Badge>
                      <AccessBadge u={u} size="xs" />
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    {canExempt(u) && (
                      <button onClick={() => exempt(u)} className="p-2 rounded-md text-zinc-500 hover:text-emerald-300 hover:bg-emerald-500/10" title="Grant free access">
                        <ShieldCheck className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {u.status === "pending" && (
                      <button onClick={() => resend(u.id, u.name)} className="p-2 rounded-md text-zinc-500 hover:text-gold hover:bg-gold/5" title="Resend">
                        <RefreshCw className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {u.id !== me?.id && (
                      <button onClick={() => remove(u.id)} className="p-2 rounded-md text-zinc-500 hover:text-red-300 hover:bg-red-500/10" title="Delete">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </Card>

      <InviteModal open={showInvite} onClose={() => setShowInvite(false)} onCreated={load} />

      {/* Resend link modal */}
      {resendModal && (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-ink-0/80 backdrop-blur-md">
          <div className="em-card p-6 sm:p-8 w-full max-w-xl" style={{ boxShadow: "0 30px 80px -20px rgba(0,0,0,0.7)" }}>
            <h3 className="font-display text-xl mb-2">
              New link for <span className="text-gold">{resendModal.name}</span>
            </h3>
            <p className="text-sm text-zinc-500 mb-6">Old links are now invalid. Valid for 7 days.</p>
            <div className="em-surface rounded-xl p-4 flex flex-col sm:flex-row gap-3">
              <div className="flex-1 min-w-0 text-xs text-zinc-300 font-mono truncate select-all">{resendModal.invitation_url}</div>
              <button
                onClick={() => copyLink(resendModal.invitation_url)}
                className="shrink-0 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-gold text-ink-0 font-semibold text-sm hover:bg-gold-hover"
                style={{ transitionProperty: "background-color", transitionDuration: "180ms" }}
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                <span>{copied ? "Copied" : "Copy link"}</span>
              </button>
            </div>
            <div className="mt-6 flex justify-end">
              <Button variant="secondary" onClick={() => setResendModal(null)}>Done</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
